// src/money.ts
var CURRENCY_SYMBOLS = {
  NGN: "\u20A6",
  GHS: "GH\u20B5",
  KES: "KSh"
};
var CURRENCY_KOBO_FACTOR = {
  NGN: 100,
  // 100 kobo = ₦1
  GHS: 100,
  // 100 pesewa = GH₵1
  KES: 100
  // 100 cents = KSh1
};
function formatMoney(amountKobo, currency) {
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const factor = CURRENCY_KOBO_FACTOR[currency] ?? 100;
  const majorAmount = amountKobo / factor;
  return `${symbol}${majorAmount.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}
function koboToNaira(kobo) {
  return kobo / 100;
}
function nairaToKobo(naira) {
  return Math.round(naira * 100);
}
function calculateItemTotal(item) {
  const variantTotal = item.selectedVariants.reduce(
    (sum, v) => sum + v.priceAdjustment,
    0
  );
  const addOnTotal = item.selectedAddOns.reduce((sum, a) => sum + a.price, 0);
  return (item.basePrice + variantTotal + addOnTotal) * item.quantity;
}
function calculateOrderTotal(items, deliveryFee, serviceFee, discount) {
  const subtotal = items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  const total = subtotal + deliveryFee + serviceFee - discount;
  return { subtotal, deliveryFee, serviceFee, discount, total: Math.max(0, total) };
}

// src/phone.ts
var COUNTRY_FORMATS = {
  NG: { prefix: "+234", localLength: 11 },
  GH: { prefix: "+233", localLength: 10 },
  KE: { prefix: "+254", localLength: 10 }
};
function formatPhone(e164) {
  if (!e164) return e164;
  if (e164.startsWith("+234")) {
    const local = "0" + e164.slice(4);
    return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
  }
  if (e164.startsWith("+233")) {
    const local = "0" + e164.slice(4);
    return `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }
  return e164;
}
function toE164(localPhone, countryCode) {
  const format2 = COUNTRY_FORMATS[countryCode];
  if (!format2) throw new Error(`Unsupported country code: ${countryCode}`);
  const cleaned = localPhone.replace(/\s/g, "");
  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.startsWith("0") && cleaned.length === format2.localLength) {
    return `${format2.prefix}${cleaned.slice(1)}`;
  }
  throw new Error(`Invalid phone number format for country ${countryCode}: ${localPhone}`);
}
function isValidPhone(phone) {
  return /^\+[1-9]\d{1,14}$/.test(phone);
}
function maskPhone(phone) {
  if (!phone || phone.length < 4) return "***";
  const countryPart = phone.startsWith("+234") ? "+234" : phone.slice(0, 4);
  const last4 = phone.slice(-4);
  return `${countryPart}***${last4}`;
}

// src/date.ts
import { format, formatDistanceToNow, isToday as fnsIsToday, isYesterday } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
var DEFAULT_TIMEZONE = "Africa/Lagos";
function formatDate(date, timezone = DEFAULT_TIMEZONE) {
  const d = typeof date === "string" ? new Date(date) : date;
  return formatInTimeZone(d, timezone, "dd MMM yyyy, h:mm a");
}
function formatRelative(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (fnsIsToday(d)) {
    return `Today at ${format(d, "h:mm a")}`;
  }
  if (isYesterday(d)) {
    return `Yesterday at ${format(d, "h:mm a")}`;
  }
  return formatDistanceToNow(d, { addSuffix: true });
}
function isToday(date) {
  return fnsIsToday(date);
}
function findSpecialHoursForDay(specialHours, timezone = DEFAULT_TIMEZONE, at = /* @__PURE__ */ new Date()) {
  if (!specialHours || specialHours.length === 0) return null;
  const todayLocal = formatInTimeZone(at, timezone, "yyyy-MM-dd");
  return specialHours.find((s) => s.date === todayLocal) ?? null;
}
function describeOrderSchedule(scheduledFor, now = /* @__PURE__ */ new Date()) {
  if (!scheduledFor) {
    return { label: "ASAP", text: "ASAP", isScheduled: false, scheduledFor: null };
  }
  const s = typeof scheduledFor === "string" ? new Date(scheduledFor) : scheduledFor;
  const diffMin = (s.getTime() - now.getTime()) / 6e4;
  if (diffMin > -1 && diffMin <= 120 && fnsIsToday(s)) {
    return { label: "WITHIN_2H", text: "Within 2 Hours", isScheduled: true, scheduledFor: s };
  }
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(s, tomorrow)) {
    return { label: "SCHEDULED", text: "Scheduled for Tomorrow", isScheduled: true, scheduledFor: s };
  }
  return {
    label: "SCHEDULED",
    text: `Scheduled for ${format(s, "EEE d MMM, h:mm a")}`,
    isScheduled: true,
    scheduledFor: s
  };
}
function canEditScheduledOrder(opts) {
  const { status, scheduledFor, cutoffMinutes, now = /* @__PURE__ */ new Date() } = opts;
  const s = (status ?? "").toLowerCase();
  if (s !== "pending" && s !== "confirmed") return false;
  if (!scheduledFor) return false;
  const when = typeof scheduledFor === "string" ? new Date(scheduledFor) : scheduledFor;
  const cutoffAt = when.getTime() - cutoffMinutes * 6e4;
  return now.getTime() < cutoffAt;
}
function isRestaurantOpen(openingHours, timezone = DEFAULT_TIMEZONE, at = /* @__PURE__ */ new Date(), specialHours) {
  const currentTime = formatInTimeZone(at, timezone, "HH:mm");
  const override = findSpecialHoursForDay(specialHours, timezone, at);
  if (override) {
    if (override.isClosed) return false;
    if (!override.open || !override.close) return false;
    return currentTime >= override.open && currentTime <= override.close;
  }
  const dayName = formatInTimeZone(at, timezone, "EEEE").toLowerCase();
  const todayHours = openingHours[dayName];
  if (!todayHours?.isOpen) return false;
  return currentTime >= todayHours.open && currentTime <= todayHours.close;
}

// src/geo.ts
var EARTH_RADIUS_KM = 6371;
function calculateDistance(p1, p2) {
  const toRad = (deg) => deg * Math.PI / 180;
  const dLat = toRad(p2.lat - p1.lat);
  const dLng = toRad(p2.lng - p1.lng);
  const lat1 = toRad(p1.lat);
  const lat2 = toRad(p2.lat);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = EARTH_RADIUS_KM * c;
  return Math.round(distance * 100) / 100;
}
function isWithinRadius(center, point, radiusKm) {
  return calculateDistance(center, point) <= radiusKm;
}
function toGeoJSON(lat, lng) {
  return { type: "Point", coordinates: [lng, lat] };
}
function fromGeoJSON(point) {
  return { lat: point.coordinates[1], lng: point.coordinates[0] };
}
function bearingBetween(p1, p2) {
  const toRad = (deg) => deg * Math.PI / 180;
  const toDeg = (rad) => rad * 180 / Math.PI;
  const dLng = toRad(p2.lng - p1.lng);
  const lat1 = toRad(p1.lat);
  const lat2 = toRad(p2.lat);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const bearing = (toDeg(Math.atan2(y, x)) + 360) % 360;
  return Math.round(bearing);
}

// src/string.ts
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function generateOrderNumber(date, sequence) {
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, "");
  const seq = String(sequence).padStart(4, "0");
  return `GXL-${dateStr}-${seq}`;
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}
function capitalize(text) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

// src/error.ts
function parseApiError(error, fallback) {
  const generic = fallback ?? "Something went wrong. Please try again.";
  if (error === null || error === void 0) {
    return generic;
  }
  const err = error;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
    return "No internet connection. Please check your network.";
  }
  if (err.code === "ETIMEDOUT") {
    return "Request timed out. Please try again.";
  }
  if (err.response?.data) {
    const data = err.response.data;
    if (typeof data.message === "string") return data.message;
    if (Array.isArray(data.message)) return data.message[0] ?? generic;
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0] ?? generic;
    }
  }
  if (error instanceof Error && error.message && !err.response) {
    return error.message;
  }
  return generic;
}

// src/validation.ts
function isValidObjectId(id) {
  return /^[0-9a-fA-F]{24}$/.test(id);
}
function maskEmail(email) {
  if (!email || !email.includes("@")) return "***";
  const [local, domain] = email.split("@");
  const first = local.charAt(0);
  const masked = `${first}${"*".repeat(Math.min(local.length - 1, 4))}`;
  return `${masked}@${domain}`;
}
export {
  bearingBetween,
  calculateDistance,
  calculateItemTotal,
  calculateOrderTotal,
  canEditScheduledOrder,
  capitalize,
  describeOrderSchedule,
  findSpecialHoursForDay,
  formatDate,
  formatMoney,
  formatPhone,
  formatRelative,
  fromGeoJSON,
  generateOrderNumber,
  isRestaurantOpen,
  isToday,
  isValidObjectId,
  isValidPhone,
  isWithinRadius,
  koboToNaira,
  maskEmail,
  maskPhone,
  nairaToKobo,
  parseApiError,
  slugify,
  toE164,
  toGeoJSON,
  truncate
};
